import React, { useState, useEffect } from "react";
import useHttp from "../../../hooks/use-http";
import { Spin, Select } from "antd";
import DynamicPieChart from "../../pieChart";
import DynamicColumnChart from "../../columnChart";
import DynamicBarChart from "../../barChart";
import DynamicLineChart from "../../lineChart";

const { Option } = Select;

const TotalHarvest = ({ isMaximized, openMaximize }) => {
  const [loading, setLoading] = useState(true);

  const [totalHarvest, setTotalHarvest] = useState(0);
  const [harvestPerCrop, setHarvestPerCrop] = useState([]);
  const [harvestPerState, setHarvestPerState] = useState([]); 

  const [viewType, setViewType] = useState("state"); 
  const [selectedState, setSelectedState] = useState("all");
  const [selectedCrop, setSelectedCrop] = useState("all");

  const [chartType, setChartType] = useState("pie");

  const { sendRequest: fetchData } = useHttp();

  useEffect(() => {
    const url = `/api/totalHarvestProcurement/?userId=${localStorage.getItem(
      "id"
    )}&orgId=${localStorage.getItem("orgId")}`;

    fetchData({ url }, (res) => {
      setTotalHarvest(res.totalHarvest || 0);
      setHarvestPerCrop(res.harvestPerCrop || []);
      setHarvestPerState(res.harvestPerState || []);  
      setLoading(false);
    });
  }, [fetchData]);

  let chartDataset = [];

  if (viewType === "state") {
    chartDataset =
      selectedState === "all"
        ? harvestPerState
        : harvestPerState.filter(
            (s) => String(s.state) === String(selectedState)
          );
  } else {
    chartDataset =
      selectedCrop === "all"
        ? harvestPerCrop
        : harvestPerCrop.filter((c) => String(c.cropName) === String(selectedCrop));
  }

  let displayValue = totalHarvest;

  if (viewType === "state" && selectedState !== "all") {
    const row = harvestPerState.find((s) => String(s.state) === String(selectedState));
    displayValue = row ? row.totalHarvest : 0;
  }

  if (viewType === "crop" && selectedCrop !== "all") {
    const row = harvestPerCrop.find((c) => String(c.cropName) === String(selectedCrop));
    displayValue = row ? row.totalHarvest : 0;
  }

  const noData = !loading && (!harvestPerCrop.length && !harvestPerState.length);

  if (isMaximized) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          padding: 20,
          background: "#f7fff7",
          borderRadius: 12,
          boxSizing: "border-box",
        }}
      >
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 18 }}>
          
          <Select
            value={viewType}
            style={{ width: 180 }}
            onChange={(v) => {
              setViewType(v);
              setSelectedCrop("all");
              setSelectedState("all");
            }}
          >
            <Option value="state">State Wise</Option>
            <Option value="crop">Crop Wise</Option>
          </Select>

          {viewType === "state" && (
            <Select
              value={selectedState}
              style={{ width: 220 }}
              onChange={(v) => setSelectedState(v)}
            >
              <Option value="all">All States</Option>
              {harvestPerState.map((s, i) => (
                <Option key={i} value={s.state}>
                  {s.state || "Unknown"}
                </Option>
              ))}
            </Select>
          )}

          {viewType === "crop" && (
            <Select
              value={selectedCrop}
              style={{ width: 220 }}
              onChange={(v) => setSelectedCrop(v)}
            >
              <Option value="all">All Crops</Option>
              {harvestPerCrop.map((c, i) => (
                <Option key={i} value={c.cropName}>
                  {c.cropName || "Unknown"}
                </Option>
              ))}
            </Select>
          )}

          <Select value={chartType} style={{ width: 160, marginLeft: "auto" }} onChange={setChartType}>
            <Option value="pie">Pie Chart</Option>
            <Option value="column">Column Chart</Option>
            <Option value="bar">Bar Chart</Option>
            <Option value="line">Line Chart</Option>
          </Select>
        </div>

        {loading ? (
          <div style={{ height: "calc(100% - 80px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Spin />
          </div>
        ) : noData ? (
          <div style={{ padding: 24, textAlign: "center", color: "#666" }}>
            <h3>No harvest data available</h3>
          </div>
        ) : (
          <>
            <div
              style={{
                textAlign: "center",
                marginBottom: 18,
                padding: 14,
                background: "#e8f8ef",
                borderRadius: 10,
              }}
            >
              <h1 style={{ fontSize: 36, fontWeight: 700, color: "#154731", margin: 0 }}>
                {Number(displayValue || 0).toLocaleString()} Kg
              </h1>
              <p style={{ margin: "6px 0 0", color: "#154731" }}>
                {viewType === "state"
                  ? selectedState === "all"
                    ? "Total Harvest (All States)"
                    : `Harvest — ${selectedState}`
                  : selectedCrop === "all"
                    ? "Total Harvest (All Crops)"
                    : `Harvest — ${selectedCrop}`}
              </p>
            </div>

            {chartType === "pie" && (
              <DynamicPieChart
                data={chartDataset}
                angleField="totalHarvest"
                colorField={viewType === "state" ? "state" : "cropName"}
                isMaximized={true}
              />
            )}

            {chartType === "column" && (
              <DynamicColumnChart
                data={chartDataset}
                xField={viewType === "state" ? "state" : "cropName"}
                yField="totalHarvest"
                seriesField={viewType === "state" ? "state" : "cropName"}
                xAxisTitle={viewType === "state" ? "State" : "Crop"}
                yAxisTitle="Harvest (Kg)"
              />
            )}

            {chartType === "bar" && (
              <DynamicBarChart
                data={chartDataset}
                xField={viewType === "state" ? "state" : "cropName"}
                yField="totalHarvest"
                seriesField={viewType === "state" ? "state" : "cropName"}
              />
            )}

            {chartType === "line" && (
              <DynamicLineChart
                data={chartDataset}
                xField={viewType === "state" ? "state" : "cropName"}
                yField="totalHarvest"
                seriesField={viewType === "state" ? "state" : "cropName"}
              />
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl shadow-md"
      style={{
        background: "#f3e8d3",
        height: 200,
        width: "100%",
        padding: 18,
        position: "relative",
      }}
    >
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", height: "100%" }}>
          <Spin />
        </div>
      ) : (
        <>
          <div style={{ textAlign: "center" }}>
            <h1 style={{ fontSize: 36, fontWeight: 700, color: "#3d5c26", margin: 0 }}>
              {totalHarvest.toLocaleString()} Kg
            </h1>
            <p style={{ fontSize: 16, color: "#3d5c26", marginTop: 4 }}>Total Harvest</p>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", padding: "0 16px", marginTop: 14, color: "#3d5c26" }}>
            <div>
              {harvestPerState[0]?.state ? (
                <>
                  <b>{harvestPerState[0].totalHarvest.toLocaleString()} Kg</b>
                  <div>{harvestPerState[0].state}</div>
                </>
              ) : (
                "NA"
              )}
            </div>

            <div>
              {harvestPerState[1]?.state ? (
                <>
                  <b>{harvestPerState[1].totalHarvest.toLocaleString()} Kg</b>
                  <div>{harvestPerState[1].state}</div>
                </>
              ) : (
                "NA"
              )}
            </div>
          </div>

          <div
            style={{
              position: "absolute",
              bottom: 10,
              right: 18,
              cursor: "pointer",
              fontWeight: 600,
              color: "#3d5c26",
            }}
            onClick={openMaximize}
          >
            More...
          </div>
        </>
      )}
    </div>
  );
};

export default TotalHarvest;
